import React, { useState } from 'react';

const OrderTicket = ({ order, isAdmin = false, onStatusChange, onConfirmPayment, onRejectPayment, onViewDetails, onAssignDriver }) => {
  const [expanded, setExpanded] = useState(false);
  const [showComprobante, setShowComprobante] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(order.status === 'entregado');

  const statusConfig = {
    pendiente: { label: 'Pendiente', color: 'bg-yellow-100 text-yellow-700 border-yellow-200', icon: '⏳' },
    aceptado: { label: 'Aceptado', color: 'bg-blue-100 text-blue-700 border-blue-200', icon: '✅' },
    preparando: { label: 'Preparando', color: 'bg-purple-100 text-purple-700 border-purple-200', icon: '👨‍🍳' },
    en_camino: { label: 'En camino', color: 'bg-orange-100 text-orange-700 border-orange-200', icon: '🚗' },
    entregado: { label: 'Entregado', color: 'bg-green-100 text-green-700 border-green-200', icon: '🎉' },
    cancelado: { label: 'Cancelado', color: 'bg-red-100 text-red-700 border-red-200', icon: '❌' }
  };

  const currentStatus = statusConfig[order.status] || statusConfig.pendiente;
  const isCancelled = order.status === 'cancelado';
  const isDelivered = order.status === 'entregado';
  const isPaymentRejected = order.payment_status === 'fallido';
  const isPaymentPending = order.payment_status === 'pagado';
  const isPaymentConfirmed = order.payment_status === 'confirmado';

  const statusOptions = [
    { value: 'pendiente', label: 'Pendiente', icon: '⏳' },
    { value: 'aceptado', label: 'Aceptado', icon: '✅' },
    { value: 'preparando', label: 'Preparando', icon: '👨‍🍳' },
    { value: 'en_camino', label: 'En camino', icon: '🚗' },
    { value: 'entregado', label: 'Entregado', icon: '🎉' },
    { value: 'cancelado', label: 'Cancelado', icon: '❌' }
  ];

  const customerInfo = order.customer_info || order.customerInfo || {};
  const items = order.items || [];
  const totalUSD = order.total_usd || order.totalUSD || 0;
  const totalBS = order.total_bs || order.totalBS || 0;
  const exchangeRate = order.exchange_rate || 40;
  const paymentData = order.payment_data || {};

  // ✅ Función auxiliar para normalizar URL
  const normalizeUrl = (url) => {
    if (!url) return null;
    if (url.startsWith('http://') || url.startsWith('https://')) return url;
    if (url.includes('i.ibb.co') || url.includes('imgbb')) {
      const match = url.match(/(i\.ibb\.co\/[^\s]+)/);
      if (match) return `https://${match[1]}`;
    }
    if (url.startsWith('/uploads')) {
      return `https://tuupastel-production.up.railway.app${url}`;
    }
    return url;
  };

  const comprobanteUrl = normalizeUrl(paymentData.comprobante);

  // ===== MODO COLAPSADO (para pedidos entregados) =====
  if (isDelivered && isCollapsed) {
    return (
      <div className="bg-gray-50 rounded-2xl shadow-sm border border-gray-200 p-4 opacity-70 hover:opacity-100 transition-opacity">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center text-lg flex-shrink-0">
              ✅
            </div>
            <div className="min-w-0 flex-1">
              <div className="font-semibold text-gray-600 truncate">
                #{order.order_number}
              </div>
              <div className="text-xs text-gray-400 truncate">
                {customerInfo.name} · ${Number(totalUSD).toFixed(2)}
              </div>
            </div>
          </div>
          <button
            onClick={() => setIsCollapsed(false)}
            className="text-xs px-3 py-1.5 bg-white border border-gray-300 text-gray-600 rounded-full hover:bg-gray-100 transition flex-shrink-0"
          >
            Ver detalles
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`rounded-2xl shadow-sm border overflow-hidden ${
      (isCancelled || isPaymentRejected) ? 'bg-gray-50 border-gray-200 opacity-70' :
      isDelivered ? 'bg-gray-50 border-gray-200' : 'bg-white border-gray-100'
    }`}>
      {/* ===== CABECERA ===== */}
      <div className={`flex items-center justify-between p-4 border-b ${
        (isCancelled || isPaymentRejected || isDelivered) ? 'bg-gray-100' : 'bg-gradient-to-r from-background to-background-alt'
      }`}>
        <div className="flex items-center gap-3">
          <div className={`w-12 h-12 rounded-full flex items-center justify-center text-2xl ${
            (isCancelled || isPaymentRejected) ? 'bg-gray-200' :
            isDelivered ? 'bg-green-100' : 'bg-gold/10'
          }`}>
            {currentStatus.icon}
          </div>
          <div>
            <div className={`font-bold text-lg ${
              (isCancelled || isPaymentRejected || isDelivered) ? 'text-gray-600' : 'text-primary'
            }`}>
              #{order.order_number || order.orderNumber || order.id}
            </div>
            <div className="text-xs text-text-secondary">
              {new Date(order.created_at || order.createdAt).toLocaleString('es-VE', {
                day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit'
              })}
            </div>
          </div>
        </div>
        <div className="text-right">
          <span className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${currentStatus.color}`}>
            {currentStatus.label}
          </span>
        </div>
      </div>

      {/* ===== BOTÓN COLAPSAR (solo para entregados) ===== */}
      {isDelivered && (
        <div className="px-4 py-2 bg-green-50 border-b border-green-100 text-center">
          <button
            onClick={() => setIsCollapsed(true)}
            className="text-xs text-green-700 hover:text-green-900 font-medium"
          >
            ▲ Colapsar
          </button>
        </div>
      )}

      {/* ===== ESTADO DE PAGO ===== */}
      <div className="px-4 py-3 bg-gray-50 border-b border-gray-100 flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm">
          <span className="text-gray-500">Pago:</span>
          {isPaymentConfirmed ? (
            <span className="text-green-600 font-semibold">✅ Confirmado</span>
          ) : isPaymentRejected ? (
            <span className="text-red-600 font-semibold">❌ Rechazado</span>
          ) : isPaymentPending ? (
            <span className="text-blue-600 font-semibold">🔍 Verificando</span>
          ) : (
            <span className="text-yellow-600 font-semibold">💰 Pendiente</span>
          )}
        </div>
        {order.payment_method && (
          <span className="text-xs text-gray-500 capitalize">
            {order.payment_method === 'pago_movil' ? '📱 Pago Móvil' :
             order.payment_method === 'transferencia' ? '🏦 Transferencia' :
             order.payment_method === 'zelle' ? '💵 Zelle' :
             order.payment_method === 'efectivo' ? '💰 Efectivo' : order.payment_method}
          </span>
        )}
      </div>

      {/* ===== PRODUCTOS ===== */}
      <div className="p-4">
        <div className="text-xs text-gray-500 uppercase tracking-wide mb-3">Productos</div>
        <div className="space-y-2">
          {items.map((item, index) => (
            <div key={index} className="flex items-start justify-between py-2 border-b border-gray-50 last:border-0">
              <div className="flex items-start gap-3 flex-1">
                <span className="text-gold font-bold text-sm bg-gold/10 px-2 py-1 rounded">
                  {item.quantity}x
                </span>
                <div>
                  <div className="font-medium text-primary text-sm">{item.name}</div>
                  {item.priceUSD && (
                    <div className="text-xs text-text-secondary">
                      ${Number(item.priceUSD).toFixed(2)} c/u
                    </div>
                  )}
                </div>
              </div>
              <div className="text-right">
                <div className="font-semibold text-primary text-sm">
                  ${(Number(item.priceUSD || 0) * item.quantity).toFixed(2)}
                </div>
                <div className="text-xs text-text-light">
                  Bs {(Number(item.priceUSD || 0) * item.quantity * exchangeRate).toFixed(2)}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Total */}
        <div className="mt-4 pt-4 border-t-2 border-dashed border-gray-200">
          <div className="flex justify-between items-center">
            <span className="font-semibold text-gray-700">Total</span>
            <div className="text-right">
              <div className="text-2xl font-bold text-gold">${Number(totalUSD).toFixed(2)}</div>
              <div className="text-xs text-text-secondary">Bs {Number(totalBS).toFixed(2)}</div>
            </div>
          </div>
        </div>

        {/* ===== COMPROBANTE DE PAGO ===== */}
        {comprobanteUrl && (
          <div className="mt-4 pt-4 border-t border-gray-100">
            <button
              onClick={() => setShowComprobante(!showComprobante)}
              className="w-full flex items-center justify-between p-3 bg-blue-50 border border-blue-200 rounded-xl hover:bg-blue-100 transition"
            >
              <div className="flex items-center gap-2">
                <span className="text-xl">💳</span>
                <span className="font-medium text-blue-800 text-sm">
                  Ver comprobante de pago
                </span>
              </div>
              <span className="text-blue-600">{showComprobante ? '▼' : '▶'}</span>
            </button>

            {showComprobante && (
              <div className="mt-3 space-y-3">
                <img
                  src={comprobanteUrl}
                  alt="Comprobante"
                  className="w-full rounded-xl border-2 border-blue-200 cursor-pointer hover:border-blue-400 transition"
                  onClick={() => window.open(comprobanteUrl, '_blank')}
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.parentElement.innerHTML = `
                      <div class="bg-red-50 border border-red-200 rounded-xl p-4 text-center text-sm text-red-600">
                        ❌ No se pudo cargar la imagen
                      </div>
                    `;
                  }}
                />
                <div className="bg-blue-50 rounded-xl p-3 space-y-1 text-xs">
                  {paymentData.paymentReference && (
                    <div><strong>Referencia:</strong> {paymentData.paymentReference}</div>
                  )}
                  {paymentData.paymentBank && (
                    <div><strong>Banco:</strong> {paymentData.paymentBank}</div>
                  )}
                  {paymentData.paymentPhone && (
                    <div><strong>Teléfono:</strong> {paymentData.paymentPhone}</div>
                  )}
                  {paymentData.paymentAmount && (
                    <div><strong>Monto:</strong> Bs {Number(paymentData.paymentAmount).toFixed(2)}</div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ===== FOTO DE ENTREGA ===== */}
        {order.delivery_photo && (
          <div className="mt-4 pt-4 border-t border-gray-100">
            <div className="text-xs text-gray-500 uppercase mb-2">📸 Foto de entrega</div>
            <img
              src={order.delivery_photo}
              alt="Entrega"
              className="w-full rounded-xl border-2 border-green-200"
            />
          </div>
        )}

        {/* ===== INFORMACIÓN DEL CLIENTE ===== */}
        <div className="mt-4 pt-4 border-t border-gray-100">
          <button
            onClick={() => setExpanded(!expanded)}
            className="w-full text-left text-sm text-gold font-medium flex items-center gap-2 hover:gap-3 transition"
          >
            <span>{expanded ? '▼' : '▶'}</span>
            <span>Información del cliente</span>
          </button>

          {expanded && (
            <div className="mt-3 p-3 bg-background-alt rounded-lg space-y-2 text-sm">
              <div><span className="text-gray-500">Nombre:</span> <span className="font-medium">{customerInfo.name || 'N/A'}</span></div>
              <div><span className="text-gray-500">Teléfono:</span> <span className="font-medium">{customerInfo.phone || 'N/A'}</span></div>
              <div><span className="text-gray-500">Dirección:</span> <span className="font-medium">{customerInfo.address || 'N/A'}</span></div>
              {customerInfo.notes && (
                <div><span className="text-gray-500">Notas:</span> <span className="italic">{customerInfo.notes}</span></div>
              )}

              {order.delivery_location?.lat && (
                <div className="pt-2 border-t border-gray-200 mt-2">
                  <span className="text-gray-500">📍 Ubicación GPS:</span>
                  <a
                    href={`https://www.google.com/maps?q=${order.delivery_location.lat},${order.delivery_location.lng}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block mt-1 text-blue-600 hover:underline text-xs"
                  >
                    Ver en Google Maps →
                  </a>
                </div>
              )}

              {order.delivery?.driverId && (
                <div className="pt-2 border-t border-gray-200 mt-2">
                  <span className="text-gray-500">🚗 Motorizado:</span>{' '}
                  <span className="font-medium">{order.delivery.driverId.name}</span>
                </div>
              )}

              {paymentData.rejectionReason && (
                <div className="pt-2 border-t border-gray-200 mt-2">
                  <span className="text-red-500">❌ Motivo de rechazo:</span>{' '}
                  <span className="italic text-red-600">{paymentData.rejectionReason}</span>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ===== ACCIONES DE PAGO ===== */}
      {isAdmin && isPaymentPending && !isCancelled && !isDelivered && (
        <div className="px-4 py-3 bg-yellow-50 border-t border-yellow-200">
          <div className="text-xs text-yellow-800 font-semibold mb-2 flex items-center gap-2">
            <span>⚠️</span> Requiere verificación de pago
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => onRejectPayment && onRejectPayment(order.id)}
              className="flex-1 py-2.5 bg-red-500 text-white rounded-full font-semibold text-sm hover:bg-red-600 transition"
            >
              ❌ Rechazar Pago
            </button>
            <button
              onClick={() => onConfirmPayment && onConfirmPayment(order.id)}
              className="flex-1 py-2.5 bg-green-500 text-white rounded-full font-semibold text-sm hover:bg-green-600 transition"
            >
              ✅ Confirmar Pago
            </button>
          </div>
        </div>
      )}

      {/* ===== ASIGNAR MOTORIZADO ===== */}
      {isAdmin && isPaymentConfirmed && !order.driver_id && !isCancelled && !isDelivered && (
        <div className="px-4 py-3 bg-blue-50 border-t border-blue-200">
          <button
            onClick={() => onAssignDriver && onAssignDriver(order)}
            className="w-full py-2.5 bg-blue-500 text-white rounded-full font-semibold text-sm hover:bg-blue-600 transition"
          >
            🚗 Asignar Motorizado
          </button>
        </div>
      )}

      {/* ===== CAMBIAR ESTADO ===== */}
      {isAdmin && !isCancelled && !isPaymentRejected && !isDelivered && (
        <div className="p-4 bg-gray-50 border-t border-gray-100">
          <div className="text-xs text-gray-500 uppercase tracking-wide mb-3">Cambiar estado</div>
          <div className="flex flex-wrap gap-2">
            {statusOptions.filter(o => o.value !== 'cancelado').map(opt => (
              <button
                key={opt.value}
                onClick={() => onStatusChange && onStatusChange(order.id, opt.value)}
                disabled={order.status === opt.value}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition flex items-center gap-1 ${
                  order.status === opt.value
                    ? 'bg-primary text-white cursor-default'
                    : 'bg-white border border-gray-200 hover:border-gold hover:bg-gold/5'
                }`}
              >
                {opt.icon} {opt.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* ===== ENTREGADO - BOTÓN PARA REACTIVAR ===== */}
      {isAdmin && isDelivered && (
        <div className="p-4 bg-gray-100 border-t border-gray-200">
          <div className="text-xs text-gray-500 uppercase tracking-wide mb-3">Cambiar estado</div>
          <div className="flex flex-wrap gap-2">
            {statusOptions.filter(o => o.value !== 'entregado' && o.value !== 'cancelado').map(opt => (
              <button
                key={opt.value}
                onClick={() => {
                  if (window.confirm(`¿Cambiar estado a ${opt.label}?`)) {
                    onStatusChange && onStatusChange(order.id, opt.value);
                  }
                }}
                className="px-3 py-1.5 rounded-full text-xs font-medium bg-white border border-gray-200 hover:border-gold hover:bg-gold/5 transition flex items-center gap-1"
              >
                {opt.icon} {opt.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* ===== CANCELADO / PAGO RECHAZADO ===== */}
      {(isCancelled || isPaymentRejected) && (
        <div className="p-4 bg-gray-100 border-t border-gray-200 text-center">
          <div className="text-sm text-gray-600 font-medium">
            {isPaymentRejected ? '❌ Pago rechazado' : '❌ Pedido cancelado'}
          </div>
          {paymentData.rejectionReason && (
            <div className="text-xs text-gray-500 mt-1 italic">
              "{paymentData.rejectionReason}"
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default OrderTicket;
